import { Box, Stack, type SxProps, Typography } from "@mui/material";
import { getDownloadURL } from "firebase/storage";
import { useState } from "react";
import { CheckboxGroupInput, number, NumberInput, TextInput, useRecordContext } from "react-admin";
import { ImageUploadField } from "../../fields/ImageUploadField";
import { type DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { gcs } from "../../utils/storage";
import { OfferFeedbackChart } from "./OfferFeedbackChart";
import { DashboardCard } from "../../misc/DashboardCard";
import { Row } from "../../misc/Row";
import { FormCard } from "../../misc/FormCard";
import useResizeObserver from "use-resize-observer";
import { LocalizedError } from "../../utils/LocalizedError";
import { l10n } from "../../l10n";
import { DrivingSchoolStamp } from "./DrivingSchoolStamp";

export function DrivingSchoolAdminForm() {
  const [card1Width, setCard1Width] = useState<number>(1);
  const [card2Width, setCard2Width] = useState<number>(1);
  const { ref: ref1 } = useResizeObserver({ box: "border-box", onResize: ({ width }) => setCard1Width(width ?? 1) });
  const { ref: ref2 } = useResizeObserver({ box: "border-box", onResize: ({ width }) => setCard2Width(width ?? 1) });
  return (
    <Box sx={{ width: "100%", boxSizing: "border-box" }}>
      <Row spacing="30px" sx={{ width: "100%", mb: "30px" }}>
        <_CustomizationFlagsForm cardRef={ref1} sx={{ width: "100%", maxWidth: "700px" }} />
        <_OtherFieldsForm cardRef={ref2} sx={{ width: "100%", maxWidth: "512px" }} />
      </Row>
      <DashboardCard
        title="Feedback zu Angeboten"
        sx={{ width: "100%", maxWidth: `${card1Width + 30 + card2Width}px` }}
      >
        <OfferFeedbackChart />
      </DashboardCard>
    </Box>
  );
}

function _CustomizationFlagsForm(props: { cardRef: React.Ref<any>; sx: SxProps }) {
  const { cardRef, sx } = props;
  return (
    <FormCard title="Customization Flags" sx={sx} cardRef={cardRef} transform={syncCancellationPolicyFlags}>
      <CheckboxGroupInput label="" source="customizations" choices={CUSTOMIZATION_FLAG_CHOICES} row={false} />
    </FormCard>
  );
}

// APP-6518, APP-6572:
// Maintain the legacy cancellationPolicyPerLesson flag for mobile-app backwards compatibility (until the mobile-app is fully adapted to the new flags).
function syncCancellationPolicyFlags(data: DrivingSchool) {
  const includesLegacyFlag = data.customizations.includes("cancellationPolicyPerLesson");
  const includesOneOfTheNewFlags = data.customizations.some(
    (flag) =>
      flag === "cancellationPolicyPerPracticalAppointment" ||
      flag === "cancellationPolicyPerTheoryAppointment" ||
      flag === "cancellationPolicyPerSimulatorAppointment",
  );
  if (includesOneOfTheNewFlags && !includesLegacyFlag) {
    data.customizations.push("cancellationPolicyPerLesson");
  } else if (!includesOneOfTheNewFlags && includesLegacyFlag) {
    data.customizations = data.customizations.filter((flag) => flag !== "cancellationPolicyPerLesson");
  }

  // Older mobile-app versions read cancellationPolicyPerPracticalAppointment for simulator appointments,
  // so keep it enabled whenever cancellationPolicyPerSimulatorAppointment is enabled (until the mobile-app is fully adapted to the new flags).
  if (
    data.customizations.includes("cancellationPolicyPerSimulatorAppointment") &&
    !data.customizations.includes("cancellationPolicyPerPracticalAppointment")
  ) {
    data.customizations.push("cancellationPolicyPerPracticalAppointment");
  }
  return data;
}

const CUSTOMIZATION_FLAG_CHOICES = Object.entries(l10n.customizationFlagLabel).reduce<{ id: string; name: string }[]>(
  // TODO[APP-6518]: Remove this filter when the mobile-app release is fully adapted to the new flags.
  (acc, [id, name]) => (id === "cancellationPolicyPerLesson" ? acc : [...acc, { id, name }]),
  [],
);

function _OtherFieldsForm(props: { cardRef: React.Ref<any>; sx: SxProps }) {
  const { cardRef, sx } = props;
  const drivingSchool = useRecordContext<DrivingSchool>();
  const [files, setFiles] = useState<File[]>([]);

  if (!drivingSchool) {
    return null;
  }

  return (
    <FormCard
      title="Simulator"
      sx={sx}
      cardRef={cardRef}
      transform={async (data) => {
        if (files.length === 1) {
          const file = files[0];
          try {
            const result = await gcs.uploadFile(file, (ext) => `/driving-schools/${drivingSchool.id}/logo/logo.${ext}`);
            data.logoUrl = await getDownloadURL(result.ref);
            setFiles([]);
          } catch (error) {
            console.error("Failed to upload image", error);
            throw new LocalizedError("Fehler beim Hochladen der Bild-Datei.");
          }
        }
        return data;
      }}
      // Show the save button if an image file was uploaded ...
      showSaveButton={files.length !== 0}
    >
      <NumberInput source="minimumSimulatorUE" label="Minimale Anzahl von Simulator Fahrstunden" validate={number()} />
      <Typography fontWeight="bold" gutterBottom>
        Empfehlungskarte
      </Typography>
      <Stack pt="1em">
        <TextInput source="referralContent.title" label="Titel" multiline resettable />
        <TextInput source="referralContent.text" label="Text" multiline resettable />
      </Stack>
      <Typography fontWeight="bold" gutterBottom>
        Fahrschul-Logo
      </Typography>
      <ImageUploadField defaultImageUrl={drivingSchool.logoUrl} onDropImage={(files) => setFiles(files)} />
      <Typography fontWeight="bold" gutterBottom pt="2em">
        Link zum Fahrschul-Profil
      </Typography>
      <TextInput source="profileUrl" label="URL" resettable fullWidth />
      <Typography fontWeight="bold" gutterBottom>
        Fahrschul-Stempel
      </Typography>
      <DrivingSchoolStamp />
      <Box height="20px" />
    </FormCard>
  );
}
